/* eslint-disable max-len */
/* eslint-disable no-nested-ternary */
import React, {
  useState, useCallback, useEffect, useLayoutEffect, useRef,
} from 'react';
import { createPortal } from 'react-dom';
import Shimmer from 'react-shimmer-effect';
import { useDispatch, useSelector } from 'react-redux';
import IcomoonReact from 'icomoon-react';
import styled from '@emotion/styled';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import { Tooltip as ReactTooltip } from 'react-tooltip';

import iconSet from '../../../../shared/images/teambuildr-selection.json';
import SwapPropDropdownSAQC from './SwapPropDropdownSAQC';
import AddPropertyColumnComponent from './AddPropertyColumnComponent';
import StatusBadgeScroller from './StatusBadgeScroller';
import hexToRgba from '../../../../shared/utils/hexToRgba';
import { SUPERSET_DEFAULTS } from '../../../../shared/constants';
import {
  saveCalendarExercise,
  saveNonDateBasedWorkoutExercise,
  updateReduxCalendarItem,
  updateReduxProgramItem,
  focusNextPreSaveItem,
  focusNextPreSaveProgramItem,
  setPreSaveTableLocalState,
} from '../../ducks/calendarActions';
import capitalizeWords from '../../../../shared/utils/capitalizeWords';
import TBportalTooltip from '../../../../shared/components/TBportalTooltip';
import Text from '../../../../shared/components/Text/Text';
import generateUniqueId from '../../../../shared/utils/generateUniqueId';
import { showErrorToast } from '../../../../shared/utils/toastManager';
import { normalizeRepArray } from '../../../../shared/utils/normalizeRepToken';
import wipeRepKeywordOnMutation from '../../../../shared/utils/wipeRepKeywordOnMutation';

// Accepts: 55, 55.5, 55-60, 55.5-60.8, 5+, 4.5+
const VALUE_PATTERN = /^\d+(\.\d+)?(-\d+(\.\d+)?|\+)?$/;
const MAX_FIELD_VALUE = 10000;
const isValidRangeToken = (tok) => {
  if (!VALUE_PATTERN.test(tok)) return false;
  const parts = tok.replace('+', '').split('-');
  if (!parts.every((p) => parseFloat(p) <= MAX_FIELD_VALUE)) return false;
  if (parts.length === 2 && parseFloat(parts[0]) > parseFloat(parts[1])) return false;
  return true;
};
// For submit: pure numbers go as floats; range/+ tokens go as strings (backend accepts both)
const toFieldValue = (token) => {
  if (/^\d+(\.\d+)?$/.test(token)) return parseFloat(token);
  return token;
};

const FormWrapper = styled('form')`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const RowValue = styled('input')`
  display: flex;
  padding: 16px;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  font-weight: 400;
  font-family: 'Nunito Sans';
  width: 100%;
  height: 32px;
  background: ${(props) => props.background};
  border: ${(props) => (!props.isError ? '1px solid #EEEEEE' : '1px solid red')};
  text-align: center;
  border-radius: 4px;
  color: ${(props) => (props.textColor ? props.textColor : '#444444')};

  ::placeholder {
    color: ${(props) => (props.textColor ? props.textColor : '#444444')};
  }
`;

const ShimmerRowValue = styled('div')`
  display: flex;
  padding: 16px;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  font-weight: 400;
  font-family: 'Nunito Sans';
  width: 100%;
  height: 32px;
  text-align: center;
  border-radius: 6px;
  color: transparent;
  margin-left: 5px;
  margin-right: 5px;
`;

const RowValueContainer = styled('div')`
  flex: 1;
  min-width: 180px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  border-radius: ${(props) => (props.shimmer ? '4px' : '0px')};
`;

const NameAndDropdown = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 3px;
  position: relative;
`;

const InfoIconWrapper = styled('div')`
  position: absolute;
  left: -20px;
  display: flex;
  align-items: center;
`;

const TooltipDataContainer = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-top: 10px;
  padding-right: 16px;
  padding-left: 16px;
  gap: 10px;
  border-radius: 4px;
  max-width: 250px;
  min-width: 250px;
`;
const TooltipDataContainerAthleteInput = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-top: 10px;
  padding-bottom: 10px;
  padding-right: 16px;
  padding-left: 16px;
  gap: 10px;
  border-radius: 4px;
  max-width: 250px;
`;

const InputWithErrorWrapper = styled('div')`
  position: relative;
  width: 100%;
`;

const InputError = styled('div')`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: -2px;
  font-size: 11px;
  font-family: 'Nunito Sans';
  color: red;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  cursor: default;
`;

/** Transposed-layout + superset-picker styled components. */
const SetsCountHeader = styled('div')`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 20px 0 22px;
`;

const SupersetWrapper = styled('div')`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const SupersetButton = styled('button')`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 8px 0 6px;
  background: #FFFFFF;
  border: 1px solid #EEEEEE;
  border-radius: 4px;
  font-family: 'Nunito Sans';
  font-size: 14px;
  color: #444444;
  cursor: pointer;
  &:focus { outline: none; }
  &:focus-visible { outline: none; box-shadow: 0 0 0 2px #BFDBFE; }
`;

const SupersetSwatch = styled('div')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 4px;
  background: ${(props) => props.background || '#EEEEEE'};
  color: #444444;
  font-size: 13px;
  font-weight: 600;
`;

const SupersetEmptyHint = styled('span')`
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 2px;
  font-family: 'Nunito Sans';
  font-size: 13px;
  color: #9E9E9E;
`;

const SupersetPopover = styled('div')`
  position: fixed;
  z-index: 1100;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 10px;
  width: 220px;
  background: white;
  border: 1px solid #EEEEEE;
  border-radius: 6px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
`;

const SupersetChip = styled('button')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border-radius: 4px;
  background: ${(props) => props.background || 'white'};
  border: 2px solid ${(props) => (props.isSelected ? '#77A7FF' : '#EEEEEE')};
  font-family: 'Nunito Sans';
  font-size: 13px;
  font-weight: 600;
  color: ${(props) => (props.muted ? '#9E9E9E' : '#444444')};
  cursor: pointer;
  &:hover { border-color: ${(props) => (props.isSelected ? '#77A7FF' : '#BDBDBD')}; }
  &:focus { outline: none; }
  &:focus-visible { outline: none; box-shadow: 0 0 0 2px #BFDBFE; }
`;

const StateBadge = styled('div')`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  height: 22px;
  padding: 0 8px;
  font-family: 'Nunito Sans';
  font-size: 12px;
  font-weight: 500;
  color: #6B6B6B;
  background: #F2F2F2;
  border-radius: 11px;
  white-space: nowrap;
`;

const SetsCountLabel = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 600;
  color: #444444;
`;

const SetsFieldGroup = styled('div')`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 12px;
`;

const SetsSubtext = styled('div')`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  white-space: nowrap;
  font-family: 'Nunito Sans';
  font-size: 12px;
  color: #9E9E9E;
`;

const SetsStepper = styled('div')`
  display: inline-flex;
  align-items: stretch;
  height: 32px;
  border: 1px solid #EEEEEE;
  border-radius: 4px;
  background: #FFFFFF;
  overflow: hidden;
`;

const StepperButton = styled('button')`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  padding: 0;
  border: none;
  background: transparent;
  color: #444444;
  font-family: 'Nunito Sans';
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  &:hover:not(:disabled) { background: #F0F0F0; }
  &:focus { outline: none; }
  &:focus-visible {
    outline: none;
    background: #E8F0FE;
    box-shadow: inset 0 0 0 2px #BFDBFE;
  }
  &:disabled {
    color: #D5D5D5;
    cursor: not-allowed;
  }
`;

const StepperInput = styled('input')`
  width: 35px;
  border: none;
  border-left: 1px solid #EEEEEE;
  border-right: 1px solid #EEEEEE;
  background: transparent;
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 600;
  color: #444444;
  text-align: center;
  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  &:focus { outline: none; }
`;

const TransposedScroll = styled('div')`
  width: 100%;
  overflow-x: auto;
  padding-left: 6px;
`;

const TransposedTable = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const TransposedRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 0;
  width: 100%;
`;

const PropertyLabelCell = styled('div')`
  display: flex;
  align-items: center;
  min-width: 120px;
  max-width: 120px;
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 400;
  color: #444444;
`;

const PropertyInputCell = styled('div')`
  display: flex;
  align-items: center;
  flex: 1 1 80px;
  min-width: 80px;
  > div {
    min-width: 0;
    width: 100%;
  }
  input {
    min-width: 0;
    width: 100%;
    box-sizing: border-box;
  }
`;

const TransposedFooter = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-top: 8px;
  padding-bottom: 4px;
`;

const AddPropertySlot = styled('div')`
  display: flex;
  width: auto;
`;

const AddPropertyTrigger = styled('div')`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border: 1px dashed #4A90E2;
  border-radius: 4px;
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 600;
  color: #4A90E2;
  background: transparent;
  cursor: pointer;
  user-select: none;
  &:hover { background: rgba(74, 144, 226, 0.05); }
`;

const AddPropertyTriggerIcon = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 4px;
  background: #4A90E2;
`;

const CompactSaveButton = styled('button')`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: #10cd8c;
  color: ${(props) => (props.shimmer ? 'transparent' : 'white')};
  border: 1px solid transparent;
  border-radius: 4px;
  font-family: 'Nunito Sans';
  font-weight: 700;
  font-size: 14px;
  line-height: 18px;
  cursor: pointer;
  &:hover { opacity: 0.92; }
  &:focus { outline: none; }
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px #FFFFFF, 0 0 0 4px #BFDBFE;
  }
`;

const PreSaveTableTransposedSAQC = ({
  coachCompletion,
  completionOnly,
  contentIndex,
  content,
  localAdditionalInfo,
  localDescription,
  swapDropdownSelectedExercise,
  assignedPace,
  date,
  dayNumber,
  measure,
}) => {
  const dispatch = useDispatch();
  const tableContainerRef = useRef(null);
  const hasMeasureMounted = useRef(false);

  const targetRef = useRef(null);
  const targetRefAthleteInputOnly = useRef(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [showAthleteInputOnlyTooltip, setShowAthleteInputOnlyTooltip] = useState(false);
  const programType = useSelector((state) => state.calendar.data.programType);
  const selectedProgramDayNumber = useSelector(
    (state) => state.calendar.data.selectedProgramDayNumber,
  );
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const effectiveDate = date || selectedDate;
  const endpointFormattedDate = moment(effectiveDate).format('YYYY-MM-DD');
  const calendarContainerScrollPositionTop = useSelector((state) => state.calendar.data.calendarContainerScrollPositionTop);
  const focusTarget = useSelector((state) => state.calendar.data.focusTarget);
  const preSaveTableLocalStateForItem = useSelector(
    (state) => state.calendar.data.preSaveTableLocalState[content.assignedId],
  );
  const [isAdding, setIsAdding] = useState(false);
  const [setNumberValue, setSetNumberValue] = useState(1);
  /** `content.groupingLetter`/`content.groupingColorCode` (Redux) are the
   * single source of truth for grouping — read directly rather than
   * mirrored into local state, so this picker and the older
   * dropdown/kebab/bulk-action superset pickers (SuperSetPickerPopup)
   * always agree, and submission always sends whichever value was picked
   * last regardless of which picker was used. */
  const [isSupersetOpen, setIsSupersetOpen] = useState(false);
  const [supersetPopoverPos, setSupersetPopoverPos] = useState(null);
  const supersetButtonRef = useRef(null);
  const supersetPopoverRef = useRef(null);
  const supersetChipRefs = useRef([]);
  const [supersetFocusIndex, setSupersetFocusIndex] = useState(0);
  const [repsInputValue, setRepsInputValue] = useState(''); // Track the raw reps input string
  const [prescribedValueInputValue, setPrescribedValueInputValue] = useState('');
  const [restPerRepInputValue, setRestPerRepInputValue] = useState('');
  const [intensityInputValue, setIntensityInputValue] = useState('');

  const [exerciseData, setExerciseData] = useState({
    reps: [],
  });

  const [errors, setErrors] = useState({
    reps: null,
    prescribedValue: null,
    restPerRep: null,
    intensity: null,
  });
  const setFieldError = useCallback((field, msg) => {
    setErrors((prev) => ({ ...prev, [field]: msg }));
  }, []);
  const errorTooltipId = `saqc-presave-error-tooltip-${content.assignedId}`;

  /** remove athlete input and prescribed value from initial state of inputs if
   * exercie is completion only
   */
  const primaryRowStateInitialStateIfExerciseIsCompletionOnly = [
    { name: 'sets' },
    { name: 'rest', label: 'Rest (s)' },
    { name: 'reps' },
    { name: 'rest per rep (secs)' },
    { name: 'intensity (bpm)' },
    { name: 'add-property', label: 'Add Property' },
  ];

  const [primaryRows, setPrimaryRows] = useState(content?.inputType === 'C' ? primaryRowStateInitialStateIfExerciseIsCompletionOnly : [
    { name: 'sets' },
    { name: 'rest', label: 'Rest (s)' },
    { name: 'athlete input', label: `${capitalizeWords(content?.inputTypeDescription)} (${content.unit === 'MS' ? 'm:s' : content.unitAbbr})` },
    { name: 'prescribed value', label: 'Prescribed Value' },
    { name: 'reps' },
    { name: 'rest per rep (secs)' },
    { name: 'intensity (bpm)' },
    { name: 'add-property', label: 'Add Property' },
  ]);

  /** remove athlete input and prescribed value from initial state of switch options if
   * exercie is completion only
   */
  const primaryRowSwitchStateInitialStateIfExerciseIsCompletionOnly = {
    sets: true,
    rest: false,
    reps: false,
    'rest per rep (secs)': false,
    'intensity (bpm)': false,
    'add-property': true,
  };

  const [primaryRowSwitch, setPrimaryRowSwitch] = useState(content?.inputType === 'C' ? primaryRowSwitchStateInitialStateIfExerciseIsCompletionOnly : {
    sets: true,
    rest: false,
    'prescribed value': false,
    reps: false,
    'rest per rep (secs)': false,
    'intensity (bpm)': false,
    'athlete input': true,
    'add-property': true,
  });

  // Turn off 'add-property' when all optional properties are enabled
  useEffect(() => {
    const allOptionalPropertiesEnabled = primaryRowSwitch.rest
        && primaryRowSwitch['prescribed value']
        && primaryRowSwitch['intensity (bpm)']
        && primaryRowSwitch.reps;

    if (allOptionalPropertiesEnabled && primaryRowSwitch['add-property']) {
      setPrimaryRowSwitch((prev) => ({
        ...prev,
        'add-property': false,
      }));
    } else if (!allOptionalPropertiesEnabled && !primaryRowSwitch['add-property']) {
      // Re-enable add-property if any optional property is turned off
      setPrimaryRowSwitch((prev) => ({
        ...prev,
        'add-property': true,
      }));
    }
  }, [
    primaryRowSwitch.rest,
    primaryRowSwitch['prescribed value'],
    primaryRowSwitch['rest per rep (secs)'],
    primaryRowSwitch['rest per rep (bpm)'],
    primaryRowSwitch['intensity (bpm)'],
    primaryRowSwitch.reps,
  ]);

  const setPrimaryRowSwitchState = (newState) => {
    setPrimaryRowSwitch(newState);
  };

  const [rest, setRest] = useState(null);
  const [intensityUnit, setIntensityUnit] = useState(null);
  const [intensityValue, setIntensityValue] = useState(null);
  const [prescribedValue, setPrescribedValue] = useState(null);
  const [restPerRep, setRestPerRep] = useState(null);
  const [restPerRepUnit, setRestPerRepUnit] = useState(null);

  /** This useEffect detects if the user chooses a different exercise
   * from the item dropdownn select and updates the pre saved item in redux
   * so that the table structure and content updates to match the selected exercise
   */
  useEffect(() => {
    if (swapDropdownSelectedExercise) {
      const updatedItem = {
        ...swapDropdownSelectedExercise,
        title: swapDropdownSelectedExercise.name,
        exerciseId: parseInt(swapDropdownSelectedExercise.id, 10),
        id: `${swapDropdownSelectedExercise.id}-${generateUniqueId()}`,
        assignedId: `${swapDropdownSelectedExercise.id}-${generateUniqueId()}`,
        preSave: true,
      };
      if (programType === 'free-form-program') {
        dispatch(updateReduxProgramItem(updatedItem, dayNumber || selectedProgramDayNumber, contentIndex));
      } else {
        dispatch(updateReduxCalendarItem(updatedItem, endpointFormattedDate, contentIndex));
      }
    }
  }, [swapDropdownSelectedExercise]);

  useEffect(() => {
    if (!primaryRowSwitch['prescribed value']) {
      setPrescribedValue(null);
      setPrescribedValueInputValue('');
    }
    if (!primaryRowSwitch['intensity (bpm)'] && !primaryRowSwitch['intensity (RPE)'] && !primaryRowSwitch['intensity (%)'] && !primaryRowSwitch['intensity (W)']) {
      setIntensityUnit(null);
      setIntensityValue(null);
      setIntensityInputValue('');
    }
    if (!primaryRowSwitch.rest) {
      setRest(null);
    }
    if (!primaryRowSwitch['rest per rep (secs)'] && !primaryRowSwitch['rest per rep (bpm)']) {
      setRestPerRep(null);
      setRestPerRepInputValue('');
      setRestPerRepUnit(null);
    }
    if (!primaryRowSwitch.reps) {
      const newExerciseData = { ...exerciseData };
      newExerciseData.reps = 0;
      setExerciseData(newExerciseData);
    }
  }, [primaryRowSwitch]);

  // Initialize state from Redux backup if it exists
  // Only runs on mount or when switching to a different item (not on every backup change)
  useEffect(() => {
    const backup = preSaveTableLocalStateForItem;
    if (backup) {
      if (backup.setNumberValue !== undefined) setSetNumberValue(backup.setNumberValue);
      if (backup.rest !== undefined) setRest(backup.rest);
      if (backup.intensityUnit !== undefined) setIntensityUnit(backup.intensityUnit);
      if (backup.intensityValue !== undefined) setIntensityValue(backup.intensityValue);
      if (backup.prescribedValue !== undefined) setPrescribedValue(backup.prescribedValue);
      if (backup.restPerRep !== undefined) setRestPerRep(backup.restPerRep);
      if (backup.restPerRepUnit !== undefined) setRestPerRepUnit(backup.restPerRepUnit);
      if (backup.repsInputValue !== undefined) setRepsInputValue(backup.repsInputValue);
      if (backup.prescribedValueInputValue !== undefined) setPrescribedValueInputValue(backup.prescribedValueInputValue);
      if (backup.restPerRepInputValue !== undefined) setRestPerRepInputValue(backup.restPerRepInputValue);
      if (backup.intensityInputValue !== undefined) setIntensityInputValue(backup.intensityInputValue);
      if (backup.exerciseData !== undefined) setExerciseData(backup.exerciseData);
      if (backup.primaryRowSwitch !== undefined) setPrimaryRowSwitch(backup.primaryRowSwitch);
      if (backup.primaryRows !== undefined) setPrimaryRows(backup.primaryRows);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content.assignedId]);

  // Save current state to Redux backup on blur
  const saveStateToRedux = useCallback(() => {
    const stateObject = {
      setNumberValue,
      rest,
      intensityUnit,
      intensityValue,
      prescribedValue,
      restPerRep,
      restPerRepUnit,
      repsInputValue,
      prescribedValueInputValue,
      restPerRepInputValue,
      intensityInputValue,
      exerciseData,
      primaryRowSwitch,
      primaryRows,
    };
    dispatch(setPreSaveTableLocalState(content.assignedId, stateObject));
  }, [
    setNumberValue, rest, intensityUnit, intensityValue, prescribedValue,
    restPerRep, restPerRepUnit, repsInputValue, prescribedValueInputValue,
    restPerRepInputValue, intensityInputValue, exerciseData,
    primaryRowSwitch, primaryRows,
    content.assignedId, dispatch,
  ]);

  // Track if we've completed initial mount/restore cycle
  const hasInitialized = useRef(false);

  // Auto-save state to Redux on every change (not just blur)
  // This ensures state is preserved when component re-renders (e.g., superset selection)
  useEffect(() => {
    // Skip the initial render cycle - only save after user makes changes
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      return;
    }
    saveStateToRedux();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    setNumberValue,
    rest,
    intensityUnit,
    intensityValue,
    prescribedValue,
    restPerRep,
    restPerRepUnit,
    prescribedValueInputValue,
    restPerRepInputValue,
    intensityInputValue,
    exerciseData,
    primaryRowSwitch,
    primaryRows,
  ]);

  const setRestFunction = (value) => {
    const intRest = parseInt(value, 10);
    // Prevent negative values
    if (intRest < 0) return;
    setRest(intRest);
  };

  const handleInputChange = (field, value) => {
    if (field === 'reps') {
      // Update the raw input value for reps field
      setRepsInputValue(value);

      // Check for partial "custom" input
      const trimmedValue = value.trim().toLowerCase();
      if (['c', 'cu', 'cus', 'cust'].includes(trimmedValue)) {
        setFieldError('reps', 'If you would like to program multiple sets, please enter them as comma separated values, e.g. 12,10,8 to match the number of sets');
        return;
      }

      // Prevent negative values for reps field
      if (value && value.toUpperCase() === 'AMAP') {
        // AMAP is valid, continue
      } else if (value && value.includes('-') && !value.startsWith('-')) {
        // Check for ranges (e.g., "8-12") but not negative numbers
        const parts = value.split('-');
        if (parts.length === 2) {
          const start = parseInt(parts[0], 10);
          const end = parseInt(parts[1], 10);
          if (start < 0 || end < 0) {
            return; // Prevent negative values in ranges
          }
        }
      } else if (value && value.includes('+') && !value.startsWith('-')) {
        // Check for minimum values (e.g., "8+") but not negative numbers
        const numPart = value.replace('+', '');
        if (parseInt(numPart, 10) < 0) {
          return; // Prevent negative minimum values
        }
      } else if (value && !Number.isNaN(Number(value))) {
        // Check for simple numbers (including negative ones)
        if (parseFloat(value) < 0) {
          return; // Prevent negative simple numbers
        }
      }

      const repsArray = value
        .split(',')
        .map((rep) => rep.trim())
        .filter(Boolean);
      const setCount = parseInt(setNumberValue, 10); // Number of sets

      const { isValid: allValid, values: normalizedReps, invalidValues } = normalizeRepArray(repsArray, ['AMAP']);

      if (!allValid) {
        if (invalidValues.length === 1) {
          setFieldError('reps', `Invalid format: "${invalidValues[0].value}" at position ${invalidValues[0].position}. Use numbers, ranges with lower value first (e.g. 8-12), minimums (e.g. 8+), or "AMAP".`);
        } else {
          const invalidList = invalidValues.map((iv) => `"${iv.value}" (pos ${iv.position})`).join(', ');
          setFieldError('reps', `Invalid format for values: ${invalidList}. Use numbers, ranges with lower value first (e.g. 8-12), minimums (e.g. 8+), or "AMAP".`);
        }
        return;
      }

      // Store the normalized array (case-fixed for AMAP); fill normalization happens on blur
      setExerciseData((prev) => ({
        ...prev,
        reps: normalizedReps,
      }));

      if (normalizedReps.length !== setCount && normalizedReps.length !== 1 && normalizedReps.length > 0) {
        setFieldError('reps', `Expected ${setCount} values or 1 value (to apply to all sets), but received ${normalizedReps.length} values.`);
      } else {
        setFieldError('reps', null);
      }
    } else if (field === 'sets') {
      // Handle sets field change and re-validate reps
      setSetNumberValue(value);

      // Re-validate existing reps against new set count
      if (exerciseData.reps && exerciseData.reps.length > 0) {
        const newSetCount = parseInt(value, 10);
        const repsArray = exerciseData.reps;

        // Check if reps count matches new set count
        if (repsArray.length !== newSetCount && repsArray.length !== 1 && repsArray.length > 0) {
          setFieldError('reps', 'Enter a number for reps to apply to all sets.');
        } else {
          setFieldError('reps', null);
        }
      }

      // Re-validate CSV fields against new set count
      const newSetCount = parseInt(value, 10);
      [
        { input: prescribedValueInputValue, field: 'prescribedValue', label: 'prescribed value' },
        { input: restPerRepInputValue, field: 'restPerRep', label: 'rest per rep' },
        { input: intensityInputValue, field: 'intensity', label: 'intensity' },
      ].forEach(({ input, field: fieldKey, label }) => {
        if (input) {
          const arr = input.split(',').map((v) => v.trim()).filter(Boolean);
          if (arr.length !== newSetCount && arr.length !== 1 && arr.length > 0) {
            setFieldError(fieldKey, `Enter a number for ${label} to apply to all sets.`);
          } else {
            setFieldError(fieldKey, null);
          }
        }
      });
    } else {
      // Update other fields normally
      setExerciseData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const typeFormatter = (typeDescription) => {
    if (typeDescription === 'lift') {
      return 'lift';
    }
    if (typeDescription === 'cardio') {
      return 'saq+c';
    }
    if (typeDescription === 'circuit') {
      return 'circuit';
    }
    return '';
  };

  const handleRepsBlur = () => {
    if (Array.isArray(exerciseData.reps)) {
      const setCount = parseInt(setNumberValue, 10);

      const { isValid: allValid, values: normalizedTokens, invalidValues } = normalizeRepArray(exerciseData.reps, ['AMAP']);

      if (!allValid) {
        if (invalidValues.length === 1) {
          setFieldError('reps', `Invalid format: "${invalidValues[0].value}" at set ${invalidValues[0].position}. Use numbers, ranges with lower value first (e.g. 8-12), minimums (e.g. 8+), or "AMAP".`);
        } else {
          const invalidList = invalidValues.map((iv) => `"${iv.value}" (set ${iv.position})`).join(', ');
          setFieldError('reps', `Invalid format for values: ${invalidList}. Use numbers, ranges with lower value first (e.g. 8-12), minimums (e.g. 8+), or "AMAP".`);
        }
        return;
      }

      // Normalize single values to fill all sets on blur
      if (normalizedTokens.length === 1) {
        const filledReps = Array(setCount).fill(normalizedTokens[0]);
        setExerciseData((prev) => ({
          ...prev,
          reps: filledReps,
        }));
        // Update the input display to show the normalized version
        setRepsInputValue(filledReps.join(', '));
        setFieldError('reps', null);
      } else if (normalizedTokens.length !== setCount) {
        setFieldError('reps', `Expected ${setCount} values or 1 value (to apply to all sets), but have ${normalizedTokens.length} values.`);
      } else {
        setExerciseData((prev) => ({
          ...prev,
          reps: normalizedTokens,
        }));
        setRepsInputValue(normalizedTokens.join(', '));
        setFieldError('reps', null);
      }
    } else {
      setFieldError('reps', `Invalid input type. Expected comma-separated values but received: ${typeof exerciseData.reps}.`);
    }
  };

  const handlePrescribedValueInputChange = (value) => {
    setPrescribedValueInputValue(value);
    const valArray = value.split(',').map((v) => v.trim()).filter(Boolean);
    const setCount = parseInt(setNumberValue, 10);
    const invalidValues = [];
    valArray.forEach((v, index) => {
      if (!isValidRangeToken(v)) {
        invalidValues.push({ value: v, position: index + 1 });
      }
    });
    if (invalidValues.length > 0) {
      if (invalidValues.length === 1) {
        setFieldError('prescribedValue', `Invalid prescribed value: "${invalidValues[0].value}" at position ${invalidValues[0].position}. Use a number, range (e.g. 4-6), or plus (e.g. 5+).`);
      } else {
        const list = invalidValues.map((iv) => `"${iv.value}" (pos ${iv.position})`).join(', ');
        setFieldError('prescribedValue', `Invalid prescribed values: ${list}. Use a number, range (e.g. 4-6), or plus (e.g. 5+).`);
      }
      return;
    }
    if (valArray.length !== setCount && valArray.length !== 1 && valArray.length > 0) {
      setFieldError('prescribedValue', 'Enter a prescribed value to apply to all sets.');
    } else {
      setFieldError('prescribedValue', null);
    }
  };

  const handlePrescribedValueBlur = () => {
    if (prescribedValueInputValue && prescribedValueInputValue.trim() !== '') {
      const valArray = prescribedValueInputValue.split(',').map((v) => v.trim()).filter(Boolean);
      const setCount = parseInt(setNumberValue, 10);
      const invalidValues = [];
      valArray.forEach((v, index) => {
        if (!isValidRangeToken(v)) {
          invalidValues.push({ value: v, position: index + 1 });
        }
      });
      if (invalidValues.length > 0) {
        if (invalidValues.length === 1) {
          setFieldError('prescribedValue', `Invalid prescribed value: "${invalidValues[0].value}" at set ${invalidValues[0].position}. Use a number, range (e.g. 4-6), or plus (e.g. 5+).`);
        } else {
          const list = invalidValues.map((iv) => `"${iv.value}" (set ${iv.position})`).join(', ');
          setFieldError('prescribedValue', `Invalid prescribed values: ${list}. Use a number, range (e.g. 4-6), or plus (e.g. 5+).`);
        }
        return;
      }
      if (valArray.length === 1) {
        const normalized = Array(setCount).fill(valArray[0]);
        setPrescribedValueInputValue(normalized.join(', '));
        setFieldError('prescribedValue', null);
      } else if (valArray.length === setCount) {
        setFieldError('prescribedValue', null);
      } else {
        setFieldError('prescribedValue', `Expected ${setCount} prescribed values or 1 value (to apply to all sets), but have ${valArray.length} values.`);
      }
    } else {
      setFieldError('prescribedValue', null);
    }
  };

  const handleRestPerRepInputChange = (value) => {
    setRestPerRepInputValue(value);
    const valArray = value.split(',').map((v) => v.trim()).filter(Boolean);
    const setCount = parseInt(setNumberValue, 10);
    const invalidValues = [];
    valArray.forEach((v, index) => {
      if (!isValidRangeToken(v)) {
        invalidValues.push({ value: v, position: index + 1 });
      }
    });
    if (invalidValues.length > 0) {
      if (invalidValues.length === 1) {
        setFieldError('restPerRep', `Invalid rest per rep: "${invalidValues[0].value}" at position ${invalidValues[0].position}. Use a number, range (e.g. 4-6), or plus (e.g. 5+).`);
      } else {
        const list = invalidValues.map((iv) => `"${iv.value}" (pos ${iv.position})`).join(', ');
        setFieldError('restPerRep', `Invalid rest per rep values: ${list}. Use a number, range (e.g. 4-6), or plus (e.g. 5+).`);
      }
      return;
    }
    if (valArray.length !== setCount && valArray.length !== 1 && valArray.length > 0) {
      setFieldError('restPerRep', 'Enter a rest per rep value to apply to all sets.');
    } else {
      setFieldError('restPerRep', null);
    }
  };

  const handleRestPerRepBlur = () => {
    if (restPerRepInputValue && restPerRepInputValue.trim() !== '') {
      const valArray = restPerRepInputValue.split(',').map((v) => v.trim()).filter(Boolean);
      const setCount = parseInt(setNumberValue, 10);
      const invalidValues = [];
      valArray.forEach((v, index) => {
        if (!isValidRangeToken(v)) {
          invalidValues.push({ value: v, position: index + 1 });
        }
      });
      if (invalidValues.length > 0) {
        if (invalidValues.length === 1) {
          setFieldError('restPerRep', `Invalid rest per rep: "${invalidValues[0].value}" at set ${invalidValues[0].position}. Use a number, range (e.g. 4-6), or plus (e.g. 5+).`);
        } else {
          const list = invalidValues.map((iv) => `"${iv.value}" (set ${iv.position})`).join(', ');
          setFieldError('restPerRep', `Invalid rest per rep values: ${list}. Use a number, range (e.g. 4-6), or plus (e.g. 5+).`);
        }
        return;
      }
      if (valArray.length === 1) {
        const normalized = Array(setCount).fill(valArray[0]);
        setRestPerRepInputValue(normalized.join(', '));
        setFieldError('restPerRep', null);
      } else if (valArray.length === setCount) {
        setFieldError('restPerRep', null);
      } else {
        setFieldError('restPerRep', `Expected ${setCount} rest per rep values or 1 value (to apply to all sets), but have ${valArray.length} values.`);
      }
    } else {
      setFieldError('restPerRep', null);
    }
  };

  const handleIntensityInputChange = (value) => {
    setIntensityInputValue(value);
    const valArray = value.split(',').map((v) => v.trim()).filter(Boolean);
    const setCount = parseInt(setNumberValue, 10);
    const invalidValues = [];
    valArray.forEach((v, index) => {
      if (!isValidRangeToken(v)) {
        invalidValues.push({ value: v, position: index + 1 });
      }
    });
    if (invalidValues.length > 0) {
      if (invalidValues.length === 1) {
        setFieldError('intensity', `Invalid intensity: "${invalidValues[0].value}" at position ${invalidValues[0].position}. Use a number, range (e.g. 4-6), or plus (e.g. 5+).`);
      } else {
        const list = invalidValues.map((iv) => `"${iv.value}" (pos ${iv.position})`).join(', ');
        setFieldError('intensity', `Invalid intensity values: ${list}. Use a number, range (e.g. 4-6), or plus (e.g. 5+).`);
      }
      return;
    }
    if (valArray.length !== setCount && valArray.length !== 1 && valArray.length > 0) {
      setFieldError('intensity', 'Enter an intensity value to apply to all sets.');
    } else {
      setFieldError('intensity', null);
    }
  };

  const handleIntensityBlur = () => {
    if (intensityInputValue && intensityInputValue.trim() !== '') {
      const valArray = intensityInputValue.split(',').map((v) => v.trim()).filter(Boolean);
      const setCount = parseInt(setNumberValue, 10);
      const invalidValues = [];
      valArray.forEach((v, index) => {
        if (!isValidRangeToken(v)) {
          invalidValues.push({ value: v, position: index + 1 });
        }
      });
      if (invalidValues.length > 0) {
        if (invalidValues.length === 1) {
          setFieldError('intensity', `Invalid intensity: "${invalidValues[0].value}" at set ${invalidValues[0].position}. Use a number, range (e.g. 4-6), or plus (e.g. 5+).`);
        } else {
          const list = invalidValues.map((iv) => `"${iv.value}" (set ${iv.position})`).join(', ');
          setFieldError('intensity', `Invalid intensity values: ${list}. Use a number, range (e.g. 4-6), or plus (e.g. 5+).`);
        }
        return;
      }
      if (valArray.length === 1) {
        const normalized = Array(setCount).fill(valArray[0]);
        setIntensityInputValue(normalized.join(', '));
        setFieldError('intensity', null);
      } else if (valArray.length === setCount) {
        setFieldError('intensity', null);
      } else {
        setFieldError('intensity', `Expected ${setCount} intensity values or 1 value (to apply to all sets), but have ${valArray.length} values.`);
      }
    } else {
      setFieldError('intensity', null);
    }
  };

  const errorAddReset = () => {
    setIsAdding(false);
  };

  const handleSaveCalendarExercise = (contentIndexParam) => {
    // Defense-in-depth: re-normalize and validate reps right before submission so no
    // case-drifted ("Amap") or invalid ("AMRAP") tokens can leak past the errors gate.
    let normalizedRepsForSubmission = exerciseData.reps;
    if (Array.isArray(exerciseData.reps) && exerciseData.reps.length > 0) {
      const { isValid: repsAreValid, values: submissionReps } = normalizeRepArray(
        exerciseData.reps,
        ['AMAP'],
      );
      if (!repsAreValid) {
        showErrorToast('Invalid reps value. Use numbers, ranges (e.g. 8-12), minimums (e.g. 8+), or "AMAP".');
        return;
      }
      normalizedRepsForSubmission = submissionReps;
    }

    const setSubmissionArray = [];
    setIsAdding(true);

    // Parse CSV inputs into normalized arrays
    const setCount = parseInt(setNumberValue, 10);
    const parseCsv = (input) => {
      if (!input || !input.trim()) return null;
      const arr = input.split(',').map((v) => v.trim()).filter(Boolean);
      if (arr.length === 1) return Array(setCount).fill(arr[0]);
      return arr;
    };
    const prescribedValues = parseCsv(prescribedValueInputValue);
    const restPerRepValues = parseCsv(restPerRepInputValue);
    const intensityValues = parseCsv(intensityInputValue);

    for (let i = 0; i < setNumberValue; i += 1) {
      let repsValue;
      if (normalizedRepsForSubmission) {
        if (Array.isArray(normalizedRepsForSubmission)) {
          repsValue = normalizedRepsForSubmission[i]
            || normalizedRepsForSubmission[normalizedRepsForSubmission.length - 1]
            || 0;
        } else if (typeof normalizedRepsForSubmission === 'string') {
          repsValue = normalizedRepsForSubmission;
        } else {
          repsValue = 0;
        }
      }
      const setObject = {
        setNumber: i + 1,
      };
      if (repsValue) {
        setObject.reps = repsValue;
      }
      if (prescribedValues) {
        setObject.prescribedValue = toFieldValue(prescribedValues[i] || prescribedValues[prescribedValues.length - 1]);
      }
      if (intensityValues) {
        setObject.intensityValue = toFieldValue(intensityValues[i] || intensityValues[intensityValues.length - 1]);
      }
      if (restPerRepValues) {
        setObject.restPerRep = toFieldValue(restPerRepValues[i] || restPerRepValues[restPerRepValues.length - 1]);
      }
      setSubmissionArray.push(setObject);
    }
    const submissionObject = {
      coachCompletion,
      completionOnly,
      /** Conditional key based on programType: "date" OR "dayNumber" */
      [programType === 'free-form-program' ? 'dayNumber' : 'date']:
          programType === 'free-form-program'
            ? dayNumber
            : moment(effectiveDate).format('YYYY-MM-DD'),
      exerciseId: content.exerciseId,
      repIntensityUnit: null,
      repRestUnit: null,
      type: typeFormatter(content.typeDescription),
      sets: setSubmissionArray,
      showSessionBreakItems: false,
    };
    if (localDescription || content.description) {
      submissionObject.description = localDescription || content.description;
    }
    if (localAdditionalInfo || content.additionalInfo) {
      submissionObject.additionalInfo = localAdditionalInfo || content.additionalInfo;
    }
    if (rest) {
      submissionObject.restTime = rest;
    }
    if (intensityUnit) {
      submissionObject.repIntensityUnit = intensityUnit;
    }
    if (restPerRepUnit) {
      submissionObject.repRestUnit = restPerRepUnit;
    }
    if (assignedPace) {
      submissionObject.pacing = assignedPace;
    }
    if (content.groupingLetter) {
      submissionObject.groupingLetter = content.groupingLetter;
      submissionObject.groupingColorCode = content.groupingColorCode;
    }
    const formattedDate = moment(effectiveDate).format('YYYY-MM-DD');
    if (programType === 'free-form-program') {
      dispatch(saveNonDateBasedWorkoutExercise(
        currentUser,
        contentIndexParam,
        submissionObject,
        dayNumber,
        errorAddReset,
        {
          onSuccess: () => {
          // Trigger focus to next preSave item
            dispatch(focusNextPreSaveProgramItem(contentIndexParam, dayNumber));
          },
          exerciseName: content.title,
        },
        content.assignedId,
      ));
    } else {
      dispatch(saveCalendarExercise(
        currentUser,
        contentIndexParam,
        submissionObject,
        formattedDate,
        errorAddReset,
        {
          onSuccess: () => {
          // Trigger focus to next preSave item
            dispatch(focusNextPreSaveItem(contentIndexParam, effectiveDate));
          },
          exerciseName: content.title,
        },
        content.assignedId,
      ));
    }
  };

  const rowValueDecider = (name, shouldShimmer) => {
    if (shouldShimmer) {
      return (
        <Shimmer>
          <RowValueContainer shimmer>
            <ShimmerRowValue />
          </RowValueContainer>
        </Shimmer>
      );
    }
    if (name === 'rest') {
      return (
        <RowValueContainer>
          <RowValue
            data-add-focus={`${content.assignedId}-rest`}
            type='number'
            value={rest}
            onChange={(e) => setRestFunction(e.target.value)}
            onBlur={saveStateToRedux}
            onKeyDown={(e) => {
              if (e.key === '-') {
                e.preventDefault();
              }
            }}
            background='#FCFCFC'
            data-focus-target={
              (name === 'rest' && primaryRowSwitch.rest && !primaryRowSwitch.sets && !primaryRowSwitch.reps)
                ? content.assignedId
                : undefined
            }
          />
        </RowValueContainer>
      );
    }
    if (name === 'prescribed value') {
      return (
        <RowValueContainer>
          <InputWithErrorWrapper>
            <RowValue
              data-add-focus={`${content.assignedId}-prescribed value`}
              isError={!!errors.prescribedValue}
              type='text'
              value={prescribedValueInputValue}
              onChange={(e) => handlePrescribedValueInputChange(e.target.value)}
              onBlur={() => {
                handlePrescribedValueBlur();
                saveStateToRedux();
              }}
              background='#FCFCFC'
              data-focus-target={
                (name === 'prescribed value' && primaryRowSwitch['prescribed value'] && !primaryRowSwitch.sets && !primaryRowSwitch.reps && !primaryRowSwitch.rest)
                  ? content.assignedId
                  : undefined
              }
            />
            {errors.prescribedValue && (
              <InputError
                data-tooltip-id={errorTooltipId}
                data-tooltip-content={errors.prescribedValue}
              >
                {errors.prescribedValue}
              </InputError>
            )}
          </InputWithErrorWrapper>
        </RowValueContainer>
      );
    }
    if (name === 'athlete input') {
      return (
        <RowValueContainer>
          <RowValue
            placeholder='-'
            /** disable the ability to edit this field */
            disabled
            background='#FCFCFC'
            textColor='#BDBDBD'
          />
        </RowValueContainer>
      );
    }
    if (name === 'rest per rep (secs)' || name === 'rest per rep (bpm)') {
      return (
        <RowValueContainer>
          <InputWithErrorWrapper>
            <RowValue
              data-add-focus={`${content.assignedId}-${name}`}
              isError={!!errors.restPerRep}
              type='text'
              value={restPerRepInputValue}
              background='#FCFCFC'
              onChange={(e) => handleRestPerRepInputChange(e.target.value)}
              onBlur={() => {
                handleRestPerRepBlur();
                saveStateToRedux();
              }}
              data-focus-target={
                ((primaryRowSwitch['rest per rep (secs)'] || primaryRowSwitch['rest per rep (bpm)']) && !primaryRowSwitch.sets && !primaryRowSwitch.reps && !primaryRowSwitch.rest && !primaryRowSwitch['prescribed value'])
                  ? content.assignedId
                  : undefined
              }
            />
            {errors.restPerRep && (
              <InputError
                data-tooltip-id={errorTooltipId}
                data-tooltip-content={errors.restPerRep}
              >
                {errors.restPerRep}
              </InputError>
            )}
          </InputWithErrorWrapper>
        </RowValueContainer>
      );
    }
    if (name === 'intensity (bpm)' || name === 'intensity (RPE)' || name === 'intensity (%)' || name === 'intensity (W)') {
      return (
        <RowValueContainer>
          <InputWithErrorWrapper>
            <RowValue
              data-add-focus={`${content.assignedId}-${name}`}
              isError={!!errors.intensity}
              type='text'
              value={intensityInputValue}
              onChange={(e) => handleIntensityInputChange(e.target.value)}
              onBlur={() => {
                handleIntensityBlur();
                saveStateToRedux();
              }}
              background='#FCFCFC'
              data-focus-target={
                ((name === 'intensity (bpm)' || name === 'intensity (RPE)' || name === 'intensity (%)' || name === 'intensity (W)')
                  && (primaryRowSwitch['intensity (bpm)'] || primaryRowSwitch['intensity (RPE)'] || primaryRowSwitch['intensity (%)'] || primaryRowSwitch['intensity (W)'])
                  && !primaryRowSwitch.sets && !primaryRowSwitch.reps && !primaryRowSwitch.rest && !primaryRowSwitch['prescribed value'] && !primaryRowSwitch['rest per rep (secs)'] && !primaryRowSwitch['rest per rep (bpm)'])
                  ? content.assignedId
                  : undefined
              }
            />
            {errors.intensity && (
              <InputError
                data-tooltip-id={errorTooltipId}
                data-tooltip-content={errors.intensity}
              >
                {errors.intensity}
              </InputError>
            )}
          </InputWithErrorWrapper>
        </RowValueContainer>
      );
    }
    return (
      <RowValueContainer>
        <InputWithErrorWrapper>
          <RowValue
            data-add-focus={name === 'reps' ? `${content.assignedId}-reps` : undefined}
            isError={name === 'reps' && !!errors.reps}
            type='text'
            background='#FCFCFC'
            value={name === 'reps' ? repsInputValue : undefined}
            onChange={(e) => handleInputChange(name, e.target.value)}
            onBlur={name === 'reps' ? () => {
              handleRepsBlur();
              saveStateToRedux();
            } : saveStateToRedux}
            onKeyDown={(e) => {
              // For reps, prevent minus at the beginning
              if (name === 'reps' && e.key === '-' && e.target.selectionStart === 0) {
                e.preventDefault();
              }
              // If the user is mutating an AMAP token they're sitting in the
              // middle of, wipe the whole token (token-only in CSV input).
              if (name === 'reps') {
                const { value, selectionStart, selectionEnd } = e.target;
                const wipeResult = wipeRepKeywordOnMutation(
                  e,
                  value,
                  selectionStart,
                  selectionEnd,
                  ['AMAP'],
                  { backspaceDeletes: true },
                );
                if (wipeResult.wiped) {
                  e.preventDefault();
                  setRepsInputValue(wipeResult.newValue);
                  handleInputChange('reps', wipeResult.newValue);
                  const inputEl = e.target;
                  requestAnimationFrame(() => {
                    inputEl.setSelectionRange(wipeResult.newCursor, wipeResult.newCursor);
                  });
                }
              }
            }}
            data-focus-target={
              // Add focus target to the first editable field that's visible (sets field takes priority)
              (name === 'reps' && primaryRowSwitch.reps && !primaryRowSwitch.sets)
                ? content.assignedId
                : undefined
            }
          />
          {name === 'reps' && errors.reps && (
            <InputError
              data-tooltip-id={errorTooltipId}
              data-tooltip-content={errors.reps}
            >
              {errors.reps}
            </InputError>
          )}
        </InputWithErrorWrapper>
      </RowValueContainer>
    );
  };

  const updateColumnName = useCallback((oldName, newName) => {
    // Prevent updating the add-property column
    if (oldName === 'add-property' || newName === 'add-property') return;

    setPrimaryRows(
      (prevRows2) => prevRows2.map(
        (row) => (row.name === oldName ? { ...row, name: newName } : row),
      ),
    );

    setPrimaryRowSwitch((prevSwitch) => ({
      ...prevSwitch,
      [oldName]: false,
      [newName]: true,
    }));
  }, []);

  const findCorrectUnit = (unitContent) => {
    if (unitContent?.unitAbbr) {
      return `(${unitContent?.unitAbbr === 'wgt' ? 'lbs' : unitContent?.unitAbbr})`;
    }
    return '';
  };

  // This useEffect is responsible for managing focus on input fields when the focusTarget changes.
  // It checks if the current focusTarget's assignedId matches the content's assignedId.
  // If they match, it finds the first input element with a data-focus-target attribute equal to the content's assignedId.
  // Once found, it sets a timeout to focus on this input element after a short delay, ensuring the DOM is ready.
  useEffect(() => {
    if (focusTarget?.assignedId === content.assignedId) {
      const firstInput = document.querySelector(`[data-focus-target="${content.assignedId}"]`);
      if (firstInput) {
        setTimeout(() => {
          firstInput.focus();
        }, 100); // Small delay to ensure DOM is ready
      }
    }
  }, [focusTarget, content.assignedId]);

  /** Re-measure the virtualized list cell when the card's vertical
   * height changes — adding/removing a property (primaryRowSwitch),
   * error messages, or the status pills. Skip the first run; the
   * CellMeasurer wrapper handles the initial measure. */
  useEffect(() => {
    if (!hasMeasureMounted.current) {
      hasMeasureMounted.current = true;
      return;
    }
    if (measure) measure();
  }, [primaryRowSwitch, errors, coachCompletion, completionOnly]);

  /** Auto-focus the first input of a newly added property (skips
   * disabled / athlete-input-only fields, which carry no data-add-focus). */
  const focusAddedProperty = useCallback((name) => {
    setTimeout(() => {
      const el = document.querySelector(`[data-add-focus="${content.assignedId}-${name}"]`);
      if (el && !el.disabled) {
        el.focus();
        if (typeof el.select === 'function') el.select();
      }
    }, 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content.assignedId]);

  /** Property label content (info icon + tooltips + SwapPropDropdownSAQC),
   * for the transposed label cell. */
  const renderPropertyLabel = (primaryRow) => {
    const hasTooltipIcon = primaryRow.name === 'reps'
      || (primaryRow.name === 'athlete input' && !completionOnly);
    return (
      <NameAndDropdown
        ref={primaryRow.name === 'reps' ? targetRef : (primaryRow.name === 'athlete input' ? targetRefAthleteInputOnly : null)}
      >
        {hasTooltipIcon && (
          <InfoIconWrapper>
            <IcomoonReact
              iconSet={iconSet}
              size={14}
              icon='info2'
              color='rgba(189, 189, 189, 1)'
              style={{ cursor: 'pointer' }}
              onMouseEnter={() => {
                if (primaryRow.name === 'reps') setShowTooltip(true);
                if (primaryRow.name === 'athlete input' && !completionOnly) setShowAthleteInputOnlyTooltip(true);
              }}
              onMouseLeave={() => {
                if (primaryRow.name === 'reps') setShowTooltip(false);
                if (primaryRow.name === 'athlete input') setShowAthleteInputOnlyTooltip(false);
              }}
            />
          </InfoIconWrapper>
        )}
        {primaryRow.name === 'reps' && showTooltip && (
          <TBportalTooltip targetRef={targetRef} backgroundColor='rgba(68, 68, 68, 1)' setShowToolTip={setShowTooltip} scrollContainerRefTopPosition={calendarContainerScrollPositionTop}>
            <TooltipDataContainer>
              <Text fontSize='13px' fontWeight={400} color='white'>Accepted Inputs</Text>
              <ul style={{ paddingLeft: '20px' }}>
                <li><Text fontSize='13px' fontWeight={400} color='white'>Absolute (e.g. 5)</Text></li>
                <li><Text fontSize='13px' fontWeight={400} color='white'>Comma Separated (e.g. 12,10,8)</Text></li>
                <li><Text fontSize='13px' fontWeight={400} color='white'>Range (e.g. 8-12)</Text></li>
                <li><Text fontSize='13px' fontWeight={400} color='white'>Minimum (e.g. 8+)</Text></li>
                <li><Text fontSize='13px' fontWeight={400} color='white'>&quot;AMAP&quot; for as many as possible</Text></li>
              </ul>
            </TooltipDataContainer>
          </TBportalTooltip>
        )}
        {primaryRow.name === 'athlete input' && showAthleteInputOnlyTooltip && !completionOnly && (
          <TBportalTooltip targetRef={targetRefAthleteInputOnly} backgroundColor='rgba(68, 68, 68, 1)' setShowToolTip={setShowAthleteInputOnlyTooltip} scrollContainerRefTopPosition={calendarContainerScrollPositionTop}>
            <TooltipDataContainerAthleteInput>
              <Text fontSize='13px' fontWeight={400} color='white'>Athlete input only</Text>
            </TooltipDataContainerAthleteInput>
          </TBportalTooltip>
        )}
        <SwapPropDropdownSAQC
          visible={primaryRow.name !== 'athlete input'}
          setPrimaryRowSwitch={setPrimaryRowSwitch}
          primaryRowSwitch={primaryRowSwitch}
          rowName={primaryRow.name}
          size={12}
          updateColumnName={updateColumnName}
          setIntensityUnit={setIntensityUnit}
          setRestPerRepUnit={setRestPerRepUnit}
          name={(primaryRow?.label || capitalizeWords(primaryRow.name))}
          unit={primaryRow.name === 'prescribed value' ? findCorrectUnit(content) : null}
        />
      </NameAndDropdown>
    );
  };

  /** SUPERSET picker — A–Z plus a clear option. */
  const SUPERSET_OPTIONS = [
    { letter: null, color: null },
    ...Object.entries(SUPERSET_DEFAULTS).map(([letter, color]) => ({ letter, color })),
  ];

  const chooseSuperset = useCallback((letter, color) => {
    setIsSupersetOpen(false);
    /** `content` (Redux) is the single source of truth for grouping —
     * dispatching here is what this row's swatch reads on next render, and
     * it's what the older dropdown/kebab/bulk-action pickers read too, so
     * both stay in sync automatically. Already durable in Redux, so no
     * separate local-state backup is needed for these two fields. */
    const updatedItem = { ...content, groupingLetter: letter, groupingColorCode: color };
    if (programType === 'free-form-program') {
      dispatch(
        updateReduxProgramItem(updatedItem, dayNumber || selectedProgramDayNumber, contentIndex),
      );
    } else {
      dispatch(updateReduxCalendarItem(updatedItem, endpointFormattedDate, contentIndex));
    }
    setTimeout(() => supersetButtonRef.current?.focus(), 0);
  }, [
    content, programType, dayNumber,
    selectedProgramDayNumber, contentIndex, endpointFormattedDate, dispatch,
  ]);

  const selectSupersetByKey = useCallback((key) => {
    if (key === 'Backspace' || key === 'Delete' || key === '0') {
      chooseSuperset(null, null);
      return true;
    }
    if (/^[a-zA-Z]$/.test(key)) {
      const letter = key.toUpperCase();
      const color = SUPERSET_DEFAULTS[letter];
      if (color) {
        chooseSuperset(letter, color);
        return true;
      }
    }
    return false;
  }, [chooseSuperset]);

  useEffect(() => {
    if (!isSupersetOpen) return undefined;
    const onMouseDown = (e) => {
      if (supersetPopoverRef.current?.contains(e.target)) return;
      if (supersetButtonRef.current?.contains(e.target)) return;
      setIsSupersetOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setIsSupersetOpen(false);
        supersetButtonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [isSupersetOpen]);

  const computeSupersetPos = useCallback(() => {
    const btn = supersetButtonRef.current;
    if (!btn) return null;
    const rect = btn.getBoundingClientRect();
    const POPOVER_WIDTH = 220;
    const VIEWPORT_PAD = 8;
    let left = rect.left;
    if (left + POPOVER_WIDTH > window.innerWidth - VIEWPORT_PAD) {
      left = Math.max(VIEWPORT_PAD, window.innerWidth - POPOVER_WIDTH - VIEWPORT_PAD);
    }
    return { top: rect.bottom + 4, left };
  }, []);

  useLayoutEffect(() => {
    if (!isSupersetOpen) return undefined;
    setSupersetPopoverPos(computeSupersetPos());
    const onScrollOrResize = () => {
      const next = computeSupersetPos();
      if (!next) return;
      setSupersetPopoverPos((prev) => {
        if (prev && prev.top === next.top && prev.left === next.left) return prev;
        return next;
      });
    };
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [isSupersetOpen, computeSupersetPos]);

  useEffect(() => {
    if (!isSupersetOpen) return;
    const idx = content.groupingLetter
      ? SUPERSET_OPTIONS.findIndex((o) => o.letter === content.groupingLetter)
      : 0;
    setSupersetFocusIndex(idx >= 0 ? idx : 0);
    setTimeout(() => supersetChipRefs.current[idx >= 0 ? idx : 0]?.focus(), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSupersetOpen]);

  const moveSupersetFocus = (delta) => {
    const next = (supersetFocusIndex + delta + SUPERSET_OPTIONS.length) % SUPERSET_OPTIONS.length;
    setSupersetFocusIndex(next);
    supersetChipRefs.current[next]?.focus();
  };

  return (
    <FormWrapper
      ref={tableContainerRef}
      onSubmit={(e) => {
        e.preventDefault();
        handleSaveCalendarExercise(contentIndex);
      }}
    >
      <SetsCountHeader>
        <SetsFieldGroup>
          <SetsCountLabel>Sets</SetsCountLabel>
          <SetsStepper>
            <StepperButton
              type='button'
              aria-label='Decrease sets'
              onClick={() => {
                const next = Math.max(1, parseInt(setNumberValue, 10) - 1);
                handleInputChange('sets', next);
                saveStateToRedux();
              }}
              disabled={parseInt(setNumberValue, 10) <= 1}
            >
              −
            </StepperButton>
            <StepperInput
              type='text'
              inputMode='numeric'
              value={setNumberValue}
              data-focus-target={primaryRowSwitch.sets ? content.assignedId : undefined}
              onFocus={(e) => e.target.select()}
              onChange={(e) => {
                const raw = e.target.value.replace(/[^0-9]/g, '');
                if (raw === '') { handleInputChange('sets', ''); return; }
                let value = parseInt(raw, 10);
                if (value > 10) value = parseInt(raw.slice(-1), 10);
                handleInputChange('sets', Math.min(10, Math.max(1, value)));
              }}
              onBlur={(e) => {
                const value = parseInt(e.target.value, 10);
                if (Number.isNaN(value) || value < 1) handleInputChange('sets', 1);
                saveStateToRedux();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); }
              }}
              aria-label='Number of sets'
            />
            <StepperButton
              type='button'
              aria-label='Increase sets'
              onClick={() => {
                const next = Math.min(10, parseInt(setNumberValue, 10) + 1);
                handleInputChange('sets', next);
                saveStateToRedux();
              }}
              disabled={parseInt(setNumberValue, 10) >= 10}
            >
              +
            </StepperButton>
          </SetsStepper>
          <SetsSubtext># of Sets: 1-10</SetsSubtext>
        </SetsFieldGroup>

        <SetsCountLabel>Superset</SetsCountLabel>
        <SupersetWrapper>
          <SupersetButton
            ref={supersetButtonRef}
            type='button'
            aria-haspopup='listbox'
            aria-expanded={isSupersetOpen}
            aria-label={content.groupingLetter ? `Superset ${content.groupingLetter}, change` : 'Select superset'}
            aria-keyshortcuts='A-Z'
            title='Type a letter (A–Z) to set a superset, or Backspace to clear'
            onClick={() => setIsSupersetOpen((v) => !v)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setIsSupersetOpen(true);
                return;
              }
              if (selectSupersetByKey(e.key)) { e.preventDefault(); }
            }}
          >
            {content.groupingLetter ? (
              <SupersetSwatch background={hexToRgba(content.groupingColorCode || SUPERSET_DEFAULTS[content.groupingLetter], 0.2)}>
                {content.groupingLetter}
              </SupersetSwatch>
            ) : (
              <SupersetEmptyHint>None</SupersetEmptyHint>
            )}
            <IcomoonReact iconSet={iconSet} size={9} icon='down-arrow' color='#9E9E9E' />
          </SupersetButton>
          {isSupersetOpen && createPortal(
            <SupersetPopover
              ref={supersetPopoverRef}
              className='transposed-superset-popover'
              role='listbox'
              aria-label='Superset grouping'
              style={{
                top: supersetPopoverPos?.top ?? -9999,
                left: supersetPopoverPos?.left ?? -9999,
                visibility: supersetPopoverPos ? 'visible' : 'hidden',
              }}
              onKeyDown={(e) => {
                if (e.key === 'ArrowRight') { e.preventDefault(); moveSupersetFocus(1); }
                else if (e.key === 'ArrowLeft') { e.preventDefault(); moveSupersetFocus(-1); }
                else if (e.key === 'ArrowDown') { e.preventDefault(); moveSupersetFocus(6); }
                else if (e.key === 'ArrowUp') { e.preventDefault(); moveSupersetFocus(-6); }
                else if (selectSupersetByKey(e.key)) { e.preventDefault(); }
              }}
            >
              {SUPERSET_OPTIONS.map((opt, idx) => {
                const isSelected = content.groupingLetter === opt.letter;
                const bg = opt.color ? hexToRgba(opt.color, 0.2) : 'white';
                return (
                  <SupersetChip
                    key={opt.letter || 'none'}
                    type='button'
                    role='option'
                    aria-selected={isSelected}
                    isSelected={isSelected}
                    background={bg}
                    muted={!opt.letter}
                    tabIndex={supersetFocusIndex === idx ? 0 : -1}
                    ref={(el) => { supersetChipRefs.current[idx] = el; }}
                    onClick={() => chooseSuperset(opt.letter, opt.color)}
                    onFocus={() => setSupersetFocusIndex(idx)}
                  >
                    {opt.letter || '—'}
                  </SupersetChip>
                );
              })}
            </SupersetPopover>,
            document.body,
          )}
        </SupersetWrapper>

        <StatusBadgeScroller>
          {coachCompletion && <StateBadge>Coach Completion</StateBadge>}
          {completionOnly && <StateBadge>Completion Only</StateBadge>}
        </StatusBadgeScroller>
      </SetsCountHeader>

      <TransposedScroll>
        <TransposedTable>
          {primaryRows.map((primaryRow) => {
            if (primaryRow.name === 'sets' || primaryRow.name === 'add-property') return null;
            if (!primaryRowSwitch[primaryRow.name]) return null;
            return (
              <TransposedRow key={`prop-${primaryRow.name}`}>
                <PropertyLabelCell>
                  {renderPropertyLabel(primaryRow)}
                </PropertyLabelCell>
                <PropertyInputCell>
                  {rowValueDecider(primaryRow.name, isAdding)}
                </PropertyInputCell>
              </TransposedRow>
            );
          })}
        </TransposedTable>
      </TransposedScroll>

      <TransposedFooter>
        <AddPropertySlot>
          <AddPropertyColumnComponent
            mode='input'
            primaryRows={primaryRows}
            primaryRowSwitch={primaryRowSwitch}
            setPrimaryRowSwitch={setPrimaryRowSwitch}
            setPrimaryRowSwitchState={setPrimaryRowSwitchState}
            containerRef={tableContainerRef}
            assignedId={content.assignedId}
            closeOnSelect
            onPropertyAdded={focusAddedProperty}
            trigger={(
              <AddPropertyTrigger>
                <AddPropertyTriggerIcon>
                  <IcomoonReact iconSet={iconSet} size={10} icon='plus' color='white' />
                </AddPropertyTriggerIcon>
                Add Property
              </AddPropertyTrigger>
            )}
          />
        </AddPropertySlot>
        {!isAdding ? (
          <CompactSaveButton type='submit'>
            SAVE
            <IcomoonReact iconSet={iconSet} size={10} icon='down-arrow' color='white' />
          </CompactSaveButton>
        ) : (
          <Shimmer>
            <CompactSaveButton shimmer>
              SAVE
              <IcomoonReact iconSet={iconSet} size={10} icon='down-arrow' color='transparent' />
            </CompactSaveButton>
          </Shimmer>
        )}
      </TransposedFooter>
      <ReactTooltip
        id={errorTooltipId}
        place='top'
        positionStrategy='fixed'
        delayShow={300}
        style={{
          zIndex: 9999,
          backgroundColor: 'rgba(68, 68, 68, 1)',
          color: 'white',
          fontFamily: 'Nunito Sans',
          fontSize: '12px',
          maxWidth: '260px',
          padding: '6px 10px',
          lineHeight: 1.3,
        }}
      />
    </FormWrapper>
  );
};

PreSaveTableTransposedSAQC.propTypes = {
  coachCompletion: PropTypes.bool.isRequired,
  completionOnly: PropTypes.bool.isRequired,
  contentIndex: PropTypes.number.isRequired,
  content: PropTypes.instanceOf(Object).isRequired,
  swapDropdownSelectedExercise: PropTypes.instanceOf(Object).isRequired,
  localAdditionalInfo: PropTypes.string,
  localDescription: PropTypes.string,
  assignedPace: PropTypes.string,
  date: PropTypes.string,
  dayNumber: PropTypes.string,
  measure: PropTypes.func,
};

PreSaveTableTransposedSAQC.defaultProps = {
  localAdditionalInfo: '',
  localDescription: '',
  assignedPace: '',
  date: null,
  dayNumber: null,
  measure: () => {},
};

export default PreSaveTableTransposedSAQC;
